// src/services/openaiService.js

/**
 * Güvenli OpenAI API Servisi
 * Artık backend API endpoint'lerine istek atıyor (Vercel Serverless Functions)
 * API key artık frontend'de tutulmuyor - güvenlik sorunu çözüldü!
 */

// API base URL - production'da otomatik, development'ta localhost
const API_BASE_URL =
    process.env.NODE_ENV === 'production'
        ? '' // Production'da (Vercel'de) relative path - Vercel otomatik olarak /api'ye yönlendirir
        : 'http://localhost:3001'; // Development'ta Express backend

/**
 * Film önerileri alma fonksiyonu
 * Backend API endpoint'ine POST isteği atar
 */
export const getAIMovieRecommendations = async (userPreferences, excludeCurrentTitles = [], excludeCurrentTmdbIds = []) => {
    const { genres = [], mood = 'belirtilmemiş', userId = null } = userPreferences;

    // Önceki önerileri al (tekrarları engellemek için)
    const recKey = userId ? `film_recommendations:${userId}` : 'film_recommendations';
    const previousRecommendations = JSON.parse(
        localStorage.getItem(recKey) || '[]'
    );

    // TMDB ID bazlı kontrol (daha güvenilir - tekrar önleme için)
    let previousTmdbIds = previousRecommendations
        .flatMap((rec) => rec.recommendations || [])
        .map((m) => m.tmdbId || m.tmdb_id)
        .filter(Boolean) // null/undefined'ları filtrele
        .slice(-50); // Son 50 filmin ID'sini al

    // Title bazlı kontrol (uyumluluk ve GPT prompt için)
    let previousTitles = previousRecommendations
        .flatMap((rec) => rec.recommendations || [])
        .map((m) => m.title)
        .slice(-30);

    // Eğer mevcut gösterilen filmler varsa (tekrar öner için), onları da ekle
    if (excludeCurrentTitles && excludeCurrentTitles.length > 0) {
        previousTitles = [...excludeCurrentTitles, ...previousTitles];
        // Duplikasyonları temizle
        previousTitles = [...new Set(previousTitles)];
    }

    // Mevcut gösterilen filmlerin TMDB ID'lerini de ekle
    if (excludeCurrentTmdbIds && excludeCurrentTmdbIds.length > 0) {
        previousTmdbIds = [...excludeCurrentTmdbIds, ...previousTmdbIds];
        // Duplikasyonları temizle
        previousTmdbIds = [...new Set(previousTmdbIds)];
    }

    try {
        // Backend API endpoint'ine POST isteği
        const apiUrl = `${API_BASE_URL}/api/recommendations`;

        console.log('🔍 API İsteği:', apiUrl);
        console.log('🔍 API_BASE_URL:', API_BASE_URL);
        console.log('🔍 NODE_ENV:', process.env.NODE_ENV);

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                genres,
                mood,
                previousTitles,        // Title bazlı (GPT prompt için)
                previousTmdbIds,       // TMDB ID bazlı (DAHA GÜVENİLİR - filtreleme için)
                userId,                // User ID (ileride kullanılabilir)
                contentType: userPreferences.contentType || 'film',
                origin: userPreferences.origin || 'yabancı',
            }),
        });

        console.log('🔍 API Yanıt Durumu:', response.status, response.statusText);

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (e) {
                errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
            }

            console.error('❌ API Hata Detayları:', {
                status: response.status,
                statusText: response.statusText,
                errorData: errorData,
                url: apiUrl
            });

            throw new Error(
                errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
            );
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('❌ Film önerileri API hatası:', error);
        console.error('❌ Hata detayları:', {
            message: error?.message,
            stack: error?.stack,
            apiUrl: `${API_BASE_URL}/api/recommendations`,
            apiBaseUrl: API_BASE_URL
        });
        throw new Error(
            `Film önerileri alınamadı: ${error?.message || 'Bilinmeyen hata'}`
        );
    }
};

/**
 * Sürpriz film/dizi önerisi alma fonksiyonu
 * Backend API endpoint'ine GET isteği atar
 */
export const getSurpriseMovieRecommendation = async (contentType = 'film', origin = null, previousTitles = []) => {
    try {
        // Origin parametresi yoksa veya null ise, backend'e "any" gönder (tüm dünyadan içerik)
        const originParam = origin || 'any';
        // Önceki önerileri query parametresi olarak ekle
        const previousTitlesParam = previousTitles.length > 0 ? `&previousTitles=${encodeURIComponent(JSON.stringify(previousTitles))}` : '';
        // Backend API endpoint'ine GET isteği (contentType, origin ve previousTitles query parametreleri ile)
        const apiUrl = `${API_BASE_URL}/api/surprise?contentType=${contentType}&origin=${originParam}${previousTitlesParam}`;

        console.log('🔍 API İsteği:', apiUrl);
        console.log('🔍 API_BASE_URL:', API_BASE_URL);
        console.log('🔍 NODE_ENV:', process.env.NODE_ENV);

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        console.log('🔍 API Yanıt Durumu:', response.status, response.statusText);

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (e) {
                errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
            }

            console.error('❌ API Hata Detayları:', {
                status: response.status,
                statusText: response.statusText,
                errorData: errorData,
                url: apiUrl
            });

            throw new Error(
                errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
            );
        }

        // Response body'yi parse etmeden önce kontrol et
        let responseText;
        try {
            responseText = await response.text();
            console.log('🔍 API Yanıt Body (raw):', responseText.substring(0, 500)); // İlk 500 karakter
        } catch (e) {
            console.error('❌ Response text okunamadı:', e);
            throw new Error('API yanıtı okunamadı');
        }

        let result;
        try {
            result = JSON.parse(responseText);
            console.log('🔍 API Yanıt (parsed):', result);
        } catch (parseError) {
            console.error('❌ JSON Parse Hatası:', parseError);
            console.error('❌ Parse edilemeyen text:', responseText);
            throw new Error(`API yanıtı geçerli JSON değil: ${parseError.message}`);
        }

        return result;
    } catch (error) {
        console.error('❌ Sürpriz film API hatası:', error);
        console.error('❌ Hata detayları:', {
            message: error?.message,
            stack: error?.stack,
            apiUrl: `${API_BASE_URL}/api/surprise`,
            apiBaseUrl: API_BASE_URL
        });
        throw new Error(
            `Sürpriz film önerisi alınamadı: ${error?.message || 'Bilinmeyen hata'}`
        );
    }
};

// Eski OpenAI instance'ı artık kullanılmıyor - kaldırıldı
// Not: Eğer başka bir yerde kullanılıyorsa, bu dosya import edilmemeli